import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useAuthenticationState } from "redux/features/auth";
import { profileSlice } from "redux/features/profile";

/*
  The context in which the portal is running: whose profile is being edited,
  and by whom. Retrievable through a hook by any component that needs it.
  
  Returns the following items from useAuthenticationState
  (see features/auth.js):

  loggedin, authenticatedAsStaff, staffUsername

  together with

  personId:              the id of the person whose profile is being edited;
                         when the user is a staff member, this id is taken
                         from the URL, otherwise, it is the id of the
                         authenticated user

  fullName:              the full name of the person whose profile is being
                         edited, obtained from the loaded profile if available,
                         but taken from the credentials during the process
                         of a new user creating a profile for the first time,
                         when personId has not yet been assigned to them.
 */

export function usePortalContext() {
  const profile = useSelector(profileSlice.selector);
  const auth = useAuthenticationState();
  const params = useParams();
  const { loggedIn, authenticatedAsStaff, staffUsername } = auth;
  const personId = staffUsername
    ? params.personId
    : auth.authenticatedAsExternal
    ? auth.id
    : null;
  const fullName =
    personId &&
    profile.id === personId &&
    profile.data &&
    profile.data.public_id === personId &&
    profile.data.full_name
      ? profile.data.full_name
      : auth.fullName;

  return {
    fullName,
    staffUsername,
    personId,
    loggedIn,
    authenticatedAsStaff,
  };
}
